import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  getAnamnesis,
  selectAnamnesis,
  selectAnamnesisHistory,
  selectStatus,
} from '../../store/slices/sickLeaveAndPrescription';
import { format } from 'date-fns';
import AnamnesisHistory from './AnamnesisHistory';
import Anamnesis from './Anamnesis';
import WithLoader from '../../components/WithLoader/WithLoader';
import backIcon from '../../assets/icons/backArrow.svg';
import styles from './Anamnesis.module.scss';
import { useTranslation } from "../../hooks/useTranslations";

type AnamnesisHistoryState = 'lastAnamnesis' | 'seeHistory' | 'selectedAnamnesis' | 'editAnamnesis';

export default function AnamnesisResultViewer({
  personId,
  personExternalId,
  style,
  disableEdit,
}: {
  personId: number;
  personExternalId: string | number;
  style?: any;
  disableEdit?: boolean;
}) {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const language = localStorage.getItem('lang');
  const anamnesisResult = useAppSelector(selectAnamnesis);
  const anamnesisHistory = useAppSelector(selectAnamnesisHistory);
  const anamnesisStatus = useAppSelector(selectStatus);
  const [anamnesisCurrentView, setAnamnesisHistoryState] = useState<
    AnamnesisHistoryState | undefined
  >('lastAnamnesis');

  const [filteredQuestionsByDate, setFilteredQuestionsByDate] = useState<any[]>([]);

  const dateOfSelectedAnamnesis =
    filteredQuestionsByDate?.[0]?.anamnesis_questions?.[0]?.datetime_create;

  const dateOLatestAnamesis = anamnesisResult?.[0]?.anamnesis_last_update;

  const handleAnamnesisHistoryBtn = (step: AnamnesisHistoryState) => {
    setAnamnesisHistoryState(step);
  };

  const selectAnamensisFromHistory = (groupId: number, itemId: number, datetime_create: string) => {
    const filteredQuestions = anamnesisHistory
      .flatMap((group: any) => ({
        ...group,
        anamnesis_questions: group.anamnesis_questions.filter(
          (q: any) => q.datetime_create === datetime_create
        ),
      }))
      .filter((group: any) => group.anamnesis_questions.length > 0);
    setAnamnesisHistoryState('selectedAnamnesis');
    setFilteredQuestionsByDate(filteredQuestions);
  };

  useEffect(() => {
    if (anamnesisCurrentView === 'seeHistory' || anamnesisCurrentView === 'selectedAnamnesis') {
      dispatch(
        getAnamnesis({
          personId: personExternalId.toString(),
          language: language || 'en',
          show_history: true,
        })
      );
    }
    if (anamnesisCurrentView === 'lastAnamnesis') {
      dispatch(
        getAnamnesis({
          personId: personExternalId.toString(),
          language: language || 'en',
        })
      ).then((response: any) => {
        if (!response.payload.data?.[0]?.anamnesis_last_update)
          setAnamnesisHistoryState('editAnamnesis');
      });
    }
  }, [anamnesisCurrentView]);

  const NavigationBtn = () => {
    if (anamnesisCurrentView === 'lastAnamnesis') {
      return (
        <div
          className={styles.historyButton}
          onClick={() => handleAnamnesisHistoryBtn('seeHistory')}
        >
          {translations("label.history")}
          <i className="black-arrow-right" />
        </div>
      );
    }
    if (anamnesisCurrentView === 'seeHistory') {
      return (
        <div className={styles.btnWrapp}>
          <button
            className={styles.backButton}
            onClick={() => handleAnamnesisHistoryBtn('lastAnamnesis')}
          >
            <img src={backIcon} alt="fullScreen" style={{ marginRight: '5px' }} />
            {translations("back")}
          </button>
        </div>
      );
    }
    if (anamnesisCurrentView === 'selectedAnamnesis') {
      return (
        <div className={styles.btnWrapp}>
          <button
            className={styles.backButton}
            onClick={() => handleAnamnesisHistoryBtn('seeHistory')}
          >
            <img src={backIcon} alt="fullScreen" style={{ marginRight: '5px' }} />
            {translations("back")}
          </button>
        </div>
      );
    }

    if (anamnesisCurrentView === 'editAnamnesis') {
      return (
        <div className={styles.btnWrapp}>
          <button
            className={styles.backButton}
            onClick={() => handleAnamnesisHistoryBtn('lastAnamnesis')}
          >
            <img src={backIcon} alt="fullScreen" style={{ marginRight: '5px' }} />
            {translations("back")}          </button>
        </div>
      );
    }
  };

  const HeaderInfo = () => {
    if (anamnesisCurrentView === 'lastAnamnesis') {
      if (disableEdit) {
        return (
          <>
            {dateOLatestAnamesis ? (
              <p className={styles.dateText} style={{ position: 'relative' }}>
                {format(new Date(dateOLatestAnamesis), 'dd.MM.yyyy')} •{' '}
                {format(new Date(dateOLatestAnamesis), 'HH:mm')}
              </p>
            ) : (
              <p></p>
            )}
            <div></div>
          </>
        );
      } else {
        return (
          <>
            {dateOLatestAnamesis ? (
              <p className={styles.dateText} style={{ position: 'relative' }}>
                {format(new Date(dateOLatestAnamesis), 'dd.MM.yyyy')} •{' '}
                {format(new Date(dateOLatestAnamesis), 'HH:mm')}
              </p>
            ) : (
              <p></p>
            )}
            <button
              className={styles.historyButton}
              onClick={() => handleAnamnesisHistoryBtn('editAnamnesis')}
            >
              {translations("edit")}
              <i
                className="editItemIcon"
                style={{ width: '16px', height: '16px', marginLeft: '8px' }}
              />
            </button>
          </>
        );
      }
    }
    return;
  };

  return (
    <WithLoader isLoading={anamnesisStatus === 'pending'}>
      <div className={styles.header}>
        {NavigationBtn()}
        {HeaderInfo()}
        {anamnesisCurrentView === 'selectedAnamnesis' && (
          <p className={styles.dateText}>
            {format(new Date(dateOfSelectedAnamnesis), 'dd.MM.yyyy')} •{' '}
            {format(new Date(dateOfSelectedAnamnesis), 'HH:mm')}
          </p>
        )}
      </div>
      {anamnesisCurrentView === 'editAnamnesis' ? (
        <Anamnesis
          anamnesisType="customer"
          personId={personId}
          personExternalId={personExternalId}
        />
      ) : anamnesisCurrentView === 'seeHistory' ? (
        <AnamnesisHistory setSelectedAnamnesis={selectAnamensisFromHistory} />
      ) : (
        <div className={styles.anamnesis}>
          <i className="arrow-icon"></i>
          {(anamnesisCurrentView === 'selectedAnamnesis'
            ? filteredQuestionsByDate
            : anamnesisResult
          ).map((group: any, index: number) => {
            return (
              <div key={group.anamnesis_question_group_id} style={{ width: '100%' }}>
                <div className={styles.titleGroup}>{`${group.anamnesis_question_group_name}`}</div>
                <div className={styles.group}>
                  {group.anamnesis_questions.map((el: any, idx: number) => {
                    return (
                      <div
                        key={idx}
                        className={styles.questionContainer}
                        style={{ padding: '10px 30px' }}
                      >
                        <div className="">
                          <p className={styles.question}>{el.question}</p>
                          <p className={`${styles.question} ${styles.italic}`}>
                            {el.answer_comment}
                          </p>
                        </div>
                        {el.answer !== null ? <div>{el.answer ? translations('yes') : translations('no')}</div> : ''}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </WithLoader>
  );
}
